/**
 * Agent type identifiers sent to and stored by the backend.
 * Used to differentiate agent behavior (e.g. content creator vs UGC video script writer).
 */
export const AGENT_TYPE = {
  CONTENT_CREATOR: "content_creator",
  UGC_VIDEO: "ugc_video",
  /** Backend / DynamoDB: communication_agent_email */
  COMMUNICATION_EMAIL: "communication_agent_email",
  /** Backend / DynamoDB: communication_agent_sms */
  COMMUNICATION_SMS: "communication_agent_sms",
  /** No fixed role/master template — general chat. */
  GENERAL: "general",
  /** AI Visibility Manager — GSC/AI-search performance + content actions (SGL-136/137). */
  VISIBILITY_MANAGER: "visibility_manager",
} as const;

export type AgentType = (typeof AGENT_TYPE)[keyof typeof AGENT_TYPE];

/**
 * Display roles shown in the UI — map to one or more backend AgentType values.
 * "receptionist" is a UI-only role that resolves to email, SMS, or both at submit time.
 */
export const AGENT_ROLE = {
  SOCIAL_MEDIA_MANAGER: AGENT_TYPE.CONTENT_CREATOR,
  RECEPTIONIST: "receptionist",
  GENERAL_ASSISTANT: AGENT_TYPE.GENERAL,
  AI_VISIBILITY_MANAGER: AGENT_TYPE.VISIBILITY_MANAGER,
} as const;

export type AgentRole = (typeof AGENT_ROLE)[keyof typeof AGENT_ROLE];

export type ReceptionistChannel = "email" | "sms" | "both";

/** Resolve a display role + optional channel to the backend AgentType values to create. */
export function resolveAgentTypes(
  role: AgentRole | null,
  receptionistChannel?: ReceptionistChannel | null
): AgentType[] {
  if (!role) return [];
  if (role === AGENT_ROLE.RECEPTIONIST) {
    if (receptionistChannel === "email") return [AGENT_TYPE.COMMUNICATION_EMAIL];
    if (receptionistChannel === "sms") return [AGENT_TYPE.COMMUNICATION_SMS];
    if (receptionistChannel === "both") return [AGENT_TYPE.COMMUNICATION_EMAIL, AGENT_TYPE.COMMUNICATION_SMS];
    return [];
  }
  return [role as AgentType];
}

/**
 * Backend may send `communication_agent` (generic), `communication_agent_email` / `_sms`,
 * or alternate casing. Used for chat welcome starters and related UX.
 */
export function isCommunicationAgentType(
  agentType: string | undefined | null
): boolean {
  if (agentType == null || typeof agentType !== "string") return false;
  const t = agentType.trim().toLowerCase();
  if (!t) return false;
  if (t === "communication_agent") return true;
  if (t === AGENT_TYPE.COMMUNICATION_EMAIL) return true;
  if (t === AGENT_TYPE.COMMUNICATION_SMS) return true;
  if (t.startsWith("communication_agent")) return true;
  return false;
}

/** Read agent type from list/detail API payloads (keys vary by API version). */
export function pickAgentTypeFromApi(agent: Record<string, unknown> | null | undefined): string | undefined {
  if (!agent || typeof agent !== "object") return undefined;
  const raw =
    agent.agent_type ??
    agent.AgentType ??
    agent.agentType;
  if (raw == null) return undefined;
  const s = String(raw).trim();
  return s || undefined;
}

export interface AgentTypeOption {
  id: AgentRole;
  label: string;
  description: string;
  /** When true, a channel sub-option (email / SMS / both) is shown after selection. */
  hasChannelPicker?: boolean;
}

export const AGENT_TYPE_OPTIONS: AgentTypeOption[] = [
  {
    id: AGENT_ROLE.SOCIAL_MEDIA_MANAGER,
    label: "Social Media Manager",
    description: "Creates social posts, images, video scripts, and researches trends across all platforms",
  },
  {
    id: AGENT_ROLE.RECEPTIONIST,
    label: "AI Receptionist",
    description: "Answers inquiries, books appointments, and follows up with leads automatically",
    hasChannelPicker: true,
  },
  {
    id: AGENT_ROLE.GENERAL_ASSISTANT,
    label: "General Assistant",
    description: "Answers questions and helps with anything — no fixed role",
  },
  {
    id: AGENT_ROLE.AI_VISIBILITY_MANAGER,
    label: "AI Visibility Manager",
    description: "Tracks where you show up in AI search and Google, and takes action to get you recommended",
  },
];
